import Image from "next/image";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useState } from "react";
import { Reservation } from "@/components/pages/account/_helpers/useFetchUserReservations";
import { addBasePathToStrapiMedia, StrapiImage } from "@/utils/strapi";
import { CheckmarkIcon } from "@/public/icons/CheckmarkIcon";
// import { ExclamationIcon } from "@/public/icons/ExclamationIcon";
import { ExclamationIcon } from "@/public/icons/ExclamationIcon";
import { LocationDetails } from "./locationDetails/LocationDetails";
import styles from "./LocationCard.module.scss";

export type LocationCardProps = {
  reservation: Reservation;
  categoryImage: StrapiImage;
};

export const LocationCard = ({
  reservation,
  categoryImage,
}: LocationCardProps) => {
  const intl = useIntl();

  const [isDetailsOpen, setDetailsOpen] = useState<boolean>(false);
  const isFlexReservation = reservation.transactionId ? false : true;
  const isReservation = reservation.bookingId ? true : false;
  const isOneWay =
    reservation.travel.startAgency.id !== reservation.travel.endAgency.id;
  const openLocationDetails = () => {
    setDetailsOpen(true);
  };

  return (
    <>
      <div
        className={styles.locationCard}
        onClick={() => openLocationDetails()}
      >
        <div className={styles.agency}>
          {reservation.travel.startAgency.name.toLowerCase()}
        </div>
        <div className={styles.content}>
          <div className={styles.infosTop}>
            <div className={styles.dates}>
              <div className={styles.start}>
                <span>{intl.formatMessage({ id: "from" })}</span>
                <span className={styles.bold}>
                  {dayjs(reservation.travel.startDate).format(
                    "ddd DD MMM YYYY"
                  )}
                </span>
                <span>{intl.formatMessage({ id: "at" })}</span>
                <span>
                  {dayjs(reservation.travel.startDate).format("HH:mm")}
                </span>
              </div>
              <div className={styles.end}>
                <span>{intl.formatMessage({ id: "from" })}</span>
                <span className={styles.bold}>
                  {dayjs(reservation.travel.endDate).format("ddd DD MMM YYYY")}
                </span>
                <span>{intl.formatMessage({ id: "at" })}</span>
                <span>{dayjs(reservation.travel.endDate).format("HH:mm")}</span>
              </div>
            </div>
            <div className={styles.vehicle}>
              <div className={styles.image}>
                <Image
                  src={addBasePathToStrapiMedia(categoryImage)}
                  alt={categoryImage.alternativeText ?? ""}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
              <div className={styles.title}>
                <span>{reservation.categoryChoice.name}</span>
              </div>
            </div>
          </div>
          <div className={styles.infosBottom}>
            <div className={styles.price}>{reservation.price} €</div>
            <div className={styles.options}>
              <div className={styles.ellipsis}>
                {isOneWay
                  ? intl.formatMessage({ id: "unlimitedKilometers" })
                  : `${reservation.travel.kilometer} km`}
                {reservation.insuranceChoice &&
                  `, ${reservation.insuranceChoice.title}`}
                {reservation.optionsChoices.map((option) => (
                  <>, {option.title}</>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.status}>
          {isReservation && !isFlexReservation && (
            <div className={styles.paid}>
              <div className={styles.icon}>
                <CheckmarkIcon color={"#37837d"} />
              </div>
              <div className={styles.label}>
                {intl.formatMessage({ id: "paid" })}
              </div>
            </div>
          )}
          {isReservation && isFlexReservation && (
            <>
              <div className={styles.booked}>
                <div className={styles.icon}>
                  <CheckmarkIcon color={"#37837d"} />
                </div>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "booked" })}
                </div>
              </div>
              <div className={styles.toPay}>
                <div className={styles.icon}>
                  <ExclamationIcon color={"#b98a38"} />
                </div>
                <div className={styles.label}>
                  {intl.formatMessage({ id: "toPay" })}
                </div>
              </div>
            </>
          )}
          {/* <div className={styles.checkInToComplete}>
            <div className={styles.icon}>
              <ExclamationIcon color={"#0069b4"} />
            </div>
            <div className={styles.label}>
              {intl.formatMessage({ id: "checkInToComplete" })}
            </div>
          </div> */}
          {!isReservation && (
            <div className={styles.toValidateBefore}>
              <div className={styles.label}>
                {intl.formatMessage(
                  { id: "toValidateBefore" },
                  {
                    value: dayjs(reservation.expiryDate).format(
                      "DD MMM YYYY à HH:mm"
                    ),
                  }
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <LocationDetails
        isModalOpen={isDetailsOpen}
        setIsOpened={setDetailsOpen}
        reservation={reservation}
        categoryImage={categoryImage}
      />
    </>
  );
};
